/**
 * Shared brand-profile schema (Phase 3, O3).
 *
 * Single source of truth for the brand-profile fields that the Setup
 * Wizard step 1 and the Brand Wizard step 2 both collect. Each wizard
 * still renders its own UI (the visual styling and layouts are
 * intentionally different — onboarding pane vs. modal overlay), but
 * they share:
 *
 *   - Field labels + placeholders (no copy drift)
 *   - The full voice-options list (so server scan results map to the
 *     same option in both wizards)
 *   - The matchVoiceOption() helper (case-insensitive contains-match,
 *     so "Professional" from the server matches "Professional +
 *     authoritative" in the setupWizard's richer label set, instead of
 *     incorrectly falling into "Other")
 *
 * The plan flagged this as the bug behind P3 (the suggestProfile API
 * shape divergence is fixed in setupWizard.js separately).
 */

/**
 * Setup Wizard's voice options — the richer phrasing reads better as a
 * first-impression onboarding choice. The substring matcher means a
 * server-side scan returning "Professional" still maps to
 * "Professional + authoritative" here without falling into "Other".
 */
export const SETUP_VOICE_OPTIONS = [
    'Friendly + approachable',
    'Professional + authoritative',
    'Witty + clever',
    'Bold + provocative',
    'Inspirational',
    'Educational',
    'Storytelling',
    'Other',
];

/**
 * Brand Wizard's voice options — terser single-word tones that match the
 * style used in dropdowns throughout the rest of the app (campaign
 * wizard, generate-month wizard, etc.).
 */
export const BRAND_VOICE_OPTIONS = [
    'Professional',
    'Casual',
    'Witty',
    'Bold',
    'Warm',
    'Friendly',
    'Inspirational',
    'Educational',
];

/**
 * Field metadata. Wizards consume the labels/placeholders so copy stays
 * consistent. `min` is just an advisory minimum — neither wizard enforces
 * it server-side.
 */
export const BRAND_PROFILE_FIELDS = {
    mission: {
        label: 'Mission — what does your brand do, and for who?',
        placeholder: 'e.g. We help small businesses grow with affordable marketing tools.',
        rows: 2,
    },
    audience: {
        label: 'Target Audience',
        placeholder: 'e.g. Small business owners, marketers...',
        rows: 2,
    },
    voice: {
        label: 'Voice — how should posts sound?',
    },
    offers: {
        label: 'Offers / Products',
        placeholder: 'e.g. Marketing tools, consulting, courses',
        rows: 2,
    },
    cta: {
        label: 'Default CTA',
        placeholder: 'e.g. Start your free trial',
    },
    keywords: {
        label: 'Brand keywords',
        placeholder: 'e.g. growth, marketing, business, tools',
    },
    avoid_words: {
        label: 'Words to avoid',
        placeholder: 'e.g. cheap, hack, spam',
    },
};

/**
 * Map a server-returned voice value (e.g. "Professional", "Witty") to the
 * best matching option in the supplied list. Falls through to a "use
 * the raw value as a custom string" sentinel when nothing matches —
 * caller decides whether to set a select to "Other" or just store as-is.
 *
 * Match priority:
 *   1. Exact case-insensitive equality.
 *   2. Case-insensitive contains-match — picks the option whose label
 *      contains the server value as a substring. This is what makes
 *      "Professional" match "Professional + authoritative".
 *   3. Reverse contains — option label is a substring of the value.
 *      Handles a server that returns a more verbose phrase than our
 *      option list expected.
 *
 * Returns { option, custom } where exactly one is set:
 *   { option: '<label from list>' }    — direct match
 *   { custom: '<server value>' }       — no match, caller handles "Other"
 */
export function matchVoiceOption(serverValue, options) {
    const v = String(serverValue || '').trim();
    if (!v) return { custom: '' };
    const lc = v.toLowerCase();
    // 1. Exact match
    let hit = options.find(opt => opt.toLowerCase() === lc);
    if (hit) return { option: hit };
    // 2. Server value is a substring of an option (e.g. "Professional"
    //    inside "Professional + authoritative")
    hit = options.find(opt => opt.toLowerCase().includes(lc));
    if (hit && hit.toLowerCase() !== 'other') return { option: hit };
    // 3. Option is a substring of server value (e.g. server returned
    //    "Bold and provocative", we have "Bold")
    hit = options.find(opt => opt.toLowerCase() !== 'other' && lc.includes(opt.toLowerCase()));
    if (hit) return { option: hit };
    return { custom: v };
}
